// Utility functions for AnimationStudio
// Build version: 2025-01-29-v4 - Dynamic API URL to prevent stale build-time values
import { getApiUrl, getApiBaseUrl } from '@/config/api';

export const API = getApiUrl();

// Convert any URL (including old absolute URLs) to work with current backend
// S3 URLs are proxied through the backend (S3 has Block Public Access enabled)
// Other URLs are proxied through the backend as well
export const getAssetUrl = (fileUrl) => {
  if (!fileUrl) return '';
  
  const backendUrl = getApiBaseUrl();
  
  // Handle old absolute URLs - extract the path and use current backend
  if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
    // Extract the path part (everything after /api/)
    const apiMatch = fileUrl.match(/\/api\/(files|exports)\/(.+)/);
    if (apiMatch) {
      // Convert to relative and prepend current backend
      return `${backendUrl}/api/${apiMatch[1]}/${apiMatch[2]}`;
    }
    
    // S3 URLs (both direct and presigned) - proxy through backend for CORS compliance
    // Pattern: https://bucket.s3.amazonaws.com/project_id/path... or with region
    // Also handles presigned URLs with query params like ?AWSAccessKeyId=...
    const s3Match = fileUrl.match(/https:\/\/([^.]+)\.s3(?:\.[^.]+)?\.amazonaws\.com\/([^?]+)/);
    if (s3Match) {
      const filePath = s3Match[2]; // This includes project_id/filename
      // Route through backend proxy - this handles CORS properly
      return `${backendUrl}/api/s3-proxy/${filePath}`;
    }
    
    // If it's a completely external URL (like fal.ai results), return as-is
    return fileUrl;
  }
  
  // Handle relative URLs
  if (fileUrl.startsWith('/api/')) {
    return `${backendUrl}${fileUrl}`;
  }
  
  return `${backendUrl}/api/${fileUrl.replace(/^\//, '')}`;
};

// Get a CORS-safe URL for canvas operations (adds cache-busting to force fresh CORS headers)
export const getCorsAssetUrl = (fileUrl) => {
  const url = getAssetUrl(fileUrl);
  if (!url) return '';
  
  // Add cache-busting parameter to ensure CORS headers are fetched fresh
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}_cors=${Date.now()}`;
};

// Get thumbnail URL (fall back to full URL if no thumbnail)
export const getAssetThumbnail = (asset) => {
  return asset.thumbnail_url ? getAssetUrl(asset.thumbnail_url) : getAssetUrl(asset.file_url);
};

// Filter assets by type and search term
export const filterAssetsByType = (assets, type, searchTerm = '') => {
  let filtered = assets.filter(a => a.type === type);
  if (searchTerm.trim()) {
    filtered = filtered.filter(a => 
      a.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }
  return filtered;
};

// Get suggested assets based on scene suggestions
export const getSuggestedAssets = (scene, assets, type) => {
  if (!scene) return [];
  const allAssets = filterAssetsByType(assets, type);
  
  if (type === 'background') {
    const suggestedLocation = scene.suggested_location || '';
    if (!suggestedLocation) return [];
    const locationLower = suggestedLocation.toLowerCase();
    return allAssets.filter(asset => {
      const nameLower = asset.name.toLowerCase();
      return nameLower.includes(locationLower) || 
             locationLower.includes(nameLower) ||
             locationLower.split(/[\s_-]+/).some(word => word.length > 2 && nameLower.includes(word)) ||
             nameLower.split(/[\s_-]+/).some(word => word.length > 2 && locationLower.includes(word));
    });
  } else if (type === 'character') {
    const suggestedCharacters = scene.suggested_characters || [];
    if (suggestedCharacters.length === 0) return [];
    return allAssets.filter(asset => {
      const nameLower = asset.name.toLowerCase();
      return suggestedCharacters.some(char => {
        const charLower = char.toLowerCase();
        return nameLower.includes(charLower) || 
               charLower.includes(nameLower) ||
               charLower.split(/[\s_-]+/).some(word => word.length > 2 && nameLower.includes(word)) ||
               nameLower.split(/[\s_-]+/).some(word => word.length > 2 && charLower.includes(word));
      });
    });
  }
  return [];
};

// Create a new character object for placement
export const createNewCharacter = (character) => ({
  asset_id: character.id,
  x: 50,
  y: 60,
  scale: 1,
  opacity: 100,
  rotation: 0,
  blur: 0,
  name: character.name,
  character_name: character.name.replace(/\.[^/.]+$/, "").toUpperCase(),
  file_url: character.file_url,
  visible: true,
  flipX: false,
  flipY: false
});

// Create a new audio track object
export const createNewAudioTrack = (audio) => ({
  asset_id: audio.id,
  name: audio.name,
  file_url: audio.file_url,
  start_time: 0,
  duration: 10
});

// Compute stage status for pipeline header
export const computeStageStatus = (selectedBackground, placedCharacters, scene, animationTakes, lipsyncTakes) => {
  const hasComposition = selectedBackground && placedCharacters.length > 0;
  const hasPreview = hasComposition && scene?.composite_image_url;
  const hasAnimation = animationTakes.length > 0 || scene?.generated_video_url;
  const hasLipsync = lipsyncTakes.length > 0 || scene?.lipsync_video_url;
  
  return {
    compose: { completed: hasComposition },
    preview: { completed: hasPreview },
    animate: { completed: hasAnimation },
    lipsync: { completed: hasLipsync },
    export: { completed: false }
  };
};

// Generate waveform data from audio buffer
export const generateWaveformData = async (audioUrl) => {
  const response = await fetch(audioUrl);
  const arrayBuffer = await response.arrayBuffer();
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
  
  const rawData = audioBuffer.getChannelData(0);
  const samples = 100;
  const blockSize = Math.floor(rawData.length / samples);
  const waveformData = [];
  
  for (let i = 0; i < samples; i++) {
    let sum = 0;
    for (let j = 0; j < blockSize; j++) {
      sum += Math.abs(rawData[i * blockSize + j]);
    }
    waveformData.push(sum / blockSize);
  }
  
  const max = Math.max(...waveformData);
  const normalized = waveformData.map(v => v / max);
  
  audioContext.close();
  return { waveform: normalized, duration: audioBuffer.duration };
};

// Constants
export const INITIAL_ASSET_LIMIT = 7;
